'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { InstallGuide } from '@/components/pwa/install-guide';
import { checkForUpdate, isStandalone, refreshPushState, registerServiceWorker, syncPush, usePwa } from '@/lib/pwa';

/** How often an open app asks the server for a newer version. */
const UPDATE_CHECK_MS = 60_000;

/**
 * App-wide PWA wiring (mounted once): service worker, install prompt capture, automatic
 * updates and push re-sync. Updates apply themselves without reinstalling: when the server
 * runs newer code, the app reloads at the next safe moment — a page change or coming back
 * to the app — and the user can apply it at once from the toast.
 */
export function PwaManager() {
  const router = useRouter();
  const pathname = usePathname();
  const updateReady = usePwa((s) => s.updateReady);
  const lastPath = useRef(pathname);
  const announced = useRef(false);

  // Installed-app detection, install prompt, service worker.
  useEffect(() => {
    const standaloneQuery = window.matchMedia('(display-mode: standalone)');
    const syncStandalone = () => usePwa.setState({ standalone: isStandalone() });
    syncStandalone();
    standaloneQuery.addEventListener('change', syncStandalone);
    const onPrompt = (e: Event) => {
      e.preventDefault(); // shown from our own button instead
      usePwa.setState({ installEvent: e as never });
    };
    const onInstalled = () => {
      (window as Window & { __invexaInstall?: Event }).__invexaInstall = undefined;
      usePwa.setState({ installEvent: null, guideOpen: false });
      toast.success('App instalado!', { description: 'Abra o Invexa pelo ícone na tela inicial.' });
    };
    // Offered before this component mounted (captured by the inline script in the root layout).
    const early = (window as Window & { __invexaInstall?: Event }).__invexaInstall;
    if (early) usePwa.setState({ installEvent: early as never });
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; url?: string } | null;
      if (data?.type === 'NAVIGATE' && data.url) router.push(new URL(data.url, window.location.origin).pathname);
      if (data?.type === 'PUSH_RESYNC') void syncPush();
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);
    void registerServiceWorker().then(() => refreshPushState());
    return () => {
      standaloneQuery.removeEventListener('change', syncStandalone);
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      navigator.serviceWorker?.removeEventListener('message', onMessage);
    };
  }, [router]);

  // Version checks: at start, every minute, and whenever the app comes back to the screen.
  useEffect(() => {
    void checkForUpdate();
    const timer = setInterval(() => void checkForUpdate(), UPDATE_CHECK_MS);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // Back from the background with a newer version waiting: reload before the user does anything.
      if (usePwa.getState().updateReady) window.location.reload();
      else void checkForUpdate().then((newer) => newer && window.location.reload());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // New version while the user is on screen: apply on the next page change, or now from the toast.
  useEffect(() => {
    if (!updateReady || announced.current) return;
    announced.current = true;
    toast('Nova versão do Invexa', {
      description: 'Atualizamos a plataforma. Toque para carregar agora.',
      duration: 15_000,
      action: { label: 'Atualizar', onClick: () => window.location.reload() },
    });
  }, [updateReady]);
  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    if (usePwa.getState().updateReady) window.location.reload();
  }, [pathname]);

  return <InstallGuide />;
}
